import { useCallback, useEffect, useRef, useState } from "react";
import { SmsSettingsPanel } from "./SmsSettingsPanel";
import { SMS_PANEL_STATES, smsPanelState } from "../memberPhone.js";
import { t } from "../strings.js";

// P3 — **THE STANDING DOOR. DECLINING IS NEVER FINAL.**
//
// Her amendment: *"The Member menu gains SMS Notifications. Any member, any time, taps it, types a
// number, gets the same consent line, presses accept, and it saves as a consented marketing number,
// joining the SMS audience instantly. A member who already has one sees it with UPDATE or REMOVE,
// and remove honours instantly, the same as STOP."*
//
// **P2's popup is a ONE-SHOT and this is PERMANENT, which is the whole difference between them.**
// The popup carries the ONCE law because it interrupts her; this panel is somewhere she chose to go,
// so it refuses nobody, has no stamp of its own to check, and is exactly as available to a member
// who pressed *Not now* last month as to one who never saw it.
//
// **App.tsx COSTS ZERO LINES.** It mounts inside `AccountMenuPanel`, which is where account controls
// already live — the same argument `PushSettings` shipped on, one preference over.
type Facts = { known: boolean; hasPhone: boolean; smsConsent: boolean };

export function SmsSettings({ language = "en" }: { language?: string }) {
  // **`known: false` UNTIL HER ROW ANSWERS, WHICH THE LAW READS AS `unknown`.** Defaulting to
  // "no number on file" would tell a member who gave hers at signup that this house has none of
  // hers, and invite her to type it again — P4-1's own ruling one preference over.
  const [facts, setFacts] = useState<Facts>({ known: false, hasPhone: false, smsConsent: false });
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const alive = useRef(true);
  // D-5's ruling: a `useState` guard is read from the render that BOUND the handler, so two taps
  // inside one frame both send. A ref is the only guard that holds — and on this control a double
  // fire is a save racing a remove.
  const sending = useRef(false);
  useEffect(() => () => { alive.current = false; }, []);

  const read = useCallback(async () => {
    try {
      const res = await fetch("/api/member/sms", { credentials: "include" });
      const d = await res.json().catch(() => null);
      // Presence, never truthiness (R53/R136/R137), and the same fail-closed direction on every
      // field: a read that threw or answered nothing leaves the panel `unknown` rather than
      // reporting an empty account.
      if (alive.current && d?.known === true) {
        setFacts({ known: true, hasPhone: d.hasPhone === true, smsConsent: d.smsConsent === true });
      }
    } catch { /* left unknown: an unread row is not a member with no number */ }
  }, []);

  useEffect(() => { void read(); }, [read]);

  // ---------------------------------------------------------------------------
  // ONE TAP EACH WAY, THROUGH ONE DOOR.
  //
  // **THE PANEL RE-READS HER ROW RATHER THAN PAINTING WHAT IT HOPED FOR** (`PushSettings`' own
  // ruling): the authority is one request away, and an optimistic paint here would report a number
  // as saved that the normaliser refused. The put-back is in a `finally`, so a thrown act cannot
  // leave a permanently dead control with her choice half-made.
  // ---------------------------------------------------------------------------
  const post = async (body: Record<string, unknown>) => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setNote("");
    try {
      const res = await fetch("/api/member/sms", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      // R39/R47: this door answers HTTP 200 with `{ ok: false }` on a refused number, so a client
      // reading the status code calls a refusal a success. Read the payload.
      const data = await res.json().catch(() => null);
      if (data?.ok === true) {
        // The field is cleared only on a landed write. Left standing after a refusal, she would be
        // looking at the number the house just told her it could not dial with nothing to correct.
        if (alive.current) { setPhone(""); setConsent(false); }
      } else {
        setNote(String(data?.message || t(language, "sms.failed")));
      }
    } catch {
      setNote(t(language, "sms.failed"));
    } finally {
      await read();
      sending.current = false;
      if (alive.current) setBusy(false);
    }
  };

  const saveNumber = () => { void post({ action: "save", phone, smsConsent: consent }); };
  const removeNumber = () => { void post({ action: "remove" }); };

  // **IT ALWAYS RENDERS**, including while her row is still coming back — `unknown` says so rather
  // than the panel appearing a moment later, because a control that pops into an account menu after
  // it opened is one she has already stopped looking for.
  return (
    <SmsSettingsPanel
      state={smsPanelState(facts) || SMS_PANEL_STATES.UNKNOWN}
      phone={phone}
      consent={consent}
      busy={busy}
      note={note}
      language={language}
      onPhone={setPhone}
      onConsent={setConsent}
      onSave={saveNumber}
      onRemove={removeNumber}
    />
  );
}
